// shared/src/prompts/_design-lock.ts
// Process gate #3 (architectural audit follow-up, 2026-05-22) — MODEL ↔
// design-§3.6.2 lockstep. The authoritative model-per-stage table lives in
// `docs/llm-chain-redesign-2026-05-25-design-notes.md` (chain V2). This file
// mirrors that table as TS data so a vitest can assert each prompt module's
// MODEL export matches.
//
// When a prompt's MODEL deliberately deviates from the design lock, add the
// step to DEVIATION_ALLOWLIST with a cross-ref to the Brief Revision artifact.
// The lockstep test will pass.

export type DeepseekModelTier = 'pro' | 'flash';

export interface ModelLockEntry {
  step:                 string;        // matches prompt module's STEP export
  designLockedTier:     DeepseekModelTier;
  designLockedFull:     'deepseek-v4-pro' | 'deepseek-v4-flash';
}

// Authoritative chain-V2 model table. Per docs/llm-chain-redesign-2026-05-25-design-notes.md:
//   · m.synthesis    = Pro (3-way adjudicator per L6 — highest-reasoning task)
//   · m.inductive    = Pro (post-floor forecaster per L19/L19a)
//   · p.arbiter      = Pro (portfolio per-ticker arbiter per L21 deep router)
//   · all other path prompts (analyst/skeptic/refine across market + per-ticker
//     contrarian/consensus) = Flash
//   · a.1.synthesis  = Flash (narrowed per L24 + Phase 6 §B)
//   · q.1.qa         = Flash (per AS-4 / docs/asymmetry-scan-redesign-brief-revision.md)
//
// Phase 6 §C (2026-05-25): V1 step entries (m.1.analyst, m.2.skeptic,
// m.3.synthesis, p.1.analyst-v1, p.2.skeptic-v1, p.3.momentum, p.4.analyst-v2,
// p.5.skeptic-v2, p.6.arbiter) removed — those prompts no longer exist.
export const DESIGN_MODEL_LOCK: ReadonlyArray<ModelLockEntry> = [
  // Market path V2
  { step: 'm.contrarian.analyst',  designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'm.contrarian.skeptic',  designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'm.contrarian.refine',   designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'm.consensus.analyst',   designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'm.consensus.skeptic',   designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'm.consensus.refine',    designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'm.synthesis',           designLockedTier: 'pro',   designLockedFull: 'deepseek-v4-pro'   },
  { step: 'm.inductive.forecast',  designLockedTier: 'pro',   designLockedFull: 'deepseek-v4-pro'   },
  // Per-ticker path V2
  { step: 'p.contrarian.analyst',  designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'p.contrarian.skeptic',  designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'p.contrarian.refine',   designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'p.consensus.analyst',   designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'p.consensus.skeptic',   designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'p.consensus.refine',    designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'p.arbiter',             designLockedTier: 'pro',   designLockedFull: 'deepseek-v4-pro'   },
  { step: 'p.inductive',           designLockedTier: 'pro',   designLockedFull: 'deepseek-v4-pro'   },
  // Basket + Q&A
  { step: 'a.1.synthesis',         designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  { step: 'q.1.qa',                designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  // Post-horizon quality scorer (Bundle C Phase 2, 2026-05-27)
  { step: 'quality.crux-scorer',   designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  // Q&A calibration qualitative judge (2026-06-16)
  { step: 'q.qa-judge',            designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  // Cross-sectional rank-IC calibration probe (2026-06-25; enrolled here per
  // audit 2026-06-29-001 DC-004 so this live, money-spending Flash stage gets the
  // same machine-checked tier guarantee + per-brief provenance as every sibling).
  { step: 'q.rank.probe',          designLockedTier: 'flash', designLockedFull: 'deepseek-v4-flash' },
  // Long-Horizon Thesis narration (2026-07-09) — the ONE LHT LLM step. Genuinely
  // design-locked to Pro (NOT a deviation): rare (commit + monthly, ~1-3 calls/mo)
  // + high-stakes durable narrative where the Pro premium is negligible in absolute
  // terms. docs/long-horizon-thesis-2026-07-09-brief-revision.md §3.5.
  { step: 'lht.thesis',            designLockedTier: 'pro',   designLockedFull: 'deepseek-v4-pro'   },
];

// Each entry = a deliberate deviation from DESIGN_MODEL_LOCK. The lockstep
// test allows the actual MODEL constant to differ from the design lock for
// these steps IFF the Brief Revision artifact exists at the cited path.
export interface DeviationAllowlistEntry {
  step:               string;
  actualFull:         'deepseek-v4-pro' | 'deepseek-v4-flash';
  briefRevisionDoc:   string;        // path under repo root
  rationale:          string;
}

// HISTORY: the 2026-05-21 c8699de Brief Revision flipped the four reasoning
// stages (m.synthesis, m.inductive.forecast, p.arbiter, p.inductive) Pro→Flash
// because Pro timed out at 240s. The 2026-05-25 chain-V2 §D rewrite (cc93611)
// re-authored those prompt files FROM SCRATCH off the Pro-locked design-notes
// and the c8699de Flash deviation was NOT re-applied, so Pro ran live for ~3
// weeks (reconciled 2026-06-16, commit 1465407 — corrected the stale "all-Flash"
// docs). A 2026-06-16 cost review then found these Pro stages dominate M+P spend
// (~78-87% of stage cost) at ~12.4× Flash/token, with the Pro promo expired.
//
// CURRENT (2026-07-09): every live SHORT-TERM chain step runs Flash; the ONLY
// live Pro step is lht.thesis (design-locked Pro, NOT a deviation — see
// DESIGN_MODEL_LOCK above; it is deliberately absent from this allowlist). The
// two INDUCTIVE
// forecasters (2026-06-16) + the m.synthesis ADJUDICATOR (2026-06-20) + the
// p.arbiter ARBITER (2026-07-01) are all deviated to Flash. The inductive flip
// was cost-driven (blind extrapolators); the m.synthesis flip ACCURACY-driven
// (Pro anti-informative — 39.6% macro hit, synthesis_advantage −0.20); the
// p.arbiter flip was taken on the shadow-A/B EQUIVALENCE evidence (92%
// directional agreement Pro-vs-Flash + Δ=0 on every graded horizon), with the
// shadow REVERSED (Pro now the challenger) to keep a regression watch on the
// slow horizons. Revert criteria + rationale:
//   · docs/inductive-forecasters-pro-to-flash-2026-06-16-brief-revision.md
//   · docs/m-synthesis-pro-to-flash-2026-06-20-brief-revision.md
//   · docs/p-arbiter-flip-to-flash-2026-07-01-brief-revision.md
export const MODEL_DEVIATION_ALLOWLIST: ReadonlyArray<DeviationAllowlistEntry> = [
  {
    step:             'm.inductive.forecast',
    actualFull:       'deepseek-v4-flash',
    briefRevisionDoc: 'docs/inductive-forecasters-pro-to-flash-2026-06-16-brief-revision.md',
    rationale:        'Blind history-only peer-hypothesis forecaster; design-lock Pro deviated to Flash for cost (2026-06-16 review). Measured-revert via calibration tab.',
  },
  {
    step:             'p.inductive',
    actualFull:       'deepseek-v4-flash',
    briefRevisionDoc: 'docs/inductive-forecasters-pro-to-flash-2026-06-16-brief-revision.md',
    rationale:        'Per-ticker twin of m.inductive; blind peer-hypothesis forecaster + the recent cost-growth driver (history floor opened ~2026-06-15). Flash for cost; measured-revert via calibration tab.',
  },
  {
    step:             'm.synthesis',
    actualFull:       'deepseek-v4-flash',
    briefRevisionDoc: 'docs/m-synthesis-pro-to-flash-2026-06-20-brief-revision.md',
    rationale:        'Accuracy-driven (not cost): the Pro adjudicator scored 39.6% on macro observables (below chance, n=189) and synthesis_advantage −0.20 vs its own Flash contrarian input (2026-06-20 review). Demoted to Flash; measured-revert via calibration tab + synthesis_quality.',
  },
  {
    step:             'p.arbiter',
    actualFull:       'deepseek-v4-flash',
    briefRevisionDoc: 'docs/p-arbiter-flip-to-flash-2026-07-01-brief-revision.md',
    rationale:        'Equivalence-driven: the Pro-vs-Flash shadow A/B showed 92% directional agreement (33/36 matched pairs) + Δ=0 on every graded horizon (1d/5d) over ~10 days. Flipped Pro→Flash to bank the ~40%-of-spend premium; shadow REVERSED (Pro = challenger) to keep watching the ungraded slow horizons. Measured-revert via K10 + signal calibration.',
  },
];
